<template>
  <section
    v-motion
    class="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8"
    aria-label="Hero"
    :initial="{ opacity: 0, y: 24 }"
    :enter="{ opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }"
  >
    <!--
      Single grid cell, siblings only: photo + scrims + copy. No absolute/inset — stacking is z-index + grid.
      Row height = max(photo aspect min-height, content height).
    -->
    <div class="grid w-full grid-cols-1 overflow-hidden rounded-lg shadow-sm">
      <img
        v-if="bannerUrl"
        :src="bannerUrl"
        :alt="name ? `${name} — restaurant banner` : 'Restaurant banner'"
        loading="eager"
        class="col-start-1 row-start-1 z-0 min-h-[220px] h-full w-full object-cover object-center md:min-h-0 md:aspect-video md:object-[center_40%]"
      />
      <div
        v-else
        class="col-start-1 row-start-1 z-0 flex min-h-[220px] w-full items-center justify-center bg-t1-neutral-muted text-sm text-t1-neutral-dark/60 md:aspect-video md:min-h-0"
      >
        {{ showOwnerPlaceholder ? $t('public.addBannerInAdmin') : '' }}
      </div>
      <div
        class="pointer-events-none col-start-1 row-start-1 z-[1] bg-gradient-to-r from-black/55 via-black/25 to-transparent"
        aria-hidden="true"
      ></div>
      <div
        class="pointer-events-none col-start-1 row-start-1 z-[1] bg-gradient-to-t from-black/90 via-black/65 to-black/45"
        aria-hidden="true"
      ></div>
      <div
        class="col-start-1 row-start-1 z-10 flex flex-col justify-end self-stretch p-6 pt-10 md:p-12 md:pt-16"
      >
        <div class="max-w-2xl text-white">
          <button
            v-if="logoUrl && canOpenOwnerLogoModal"
            type="button"
            class="mb-4 inline-block rounded-lg border-0 bg-transparent p-0 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900/80"
            :aria-label="$t('public.ownerEditLogoAria')"
            data-testid="public-hero-logo"
            @click="openOwnerLogoModal"
          >
            <div class="inline-block rounded-lg border-2 border-t1-border bg-white/80 p-2 shadow-sm">
              <img
                :src="logoUrl"
                :alt="name || 'Restaurant'"
                class="max-h-20 w-auto max-w-[12rem] rounded-lg object-contain md:max-h-28"
              />
            </div>
          </button>
          <div v-else-if="logoUrl" class="mb-4" data-testid="public-hero-logo">
            <div class="inline-block rounded-lg border-2 border-t1-border bg-white/80 p-2 shadow-sm">
              <img
                :src="logoUrl"
                :alt="name || 'Restaurant'"
                class="max-h-20 w-auto max-w-[12rem] rounded-lg object-contain md:max-h-28"
              />
            </div>
          </div>
          <button
            v-else-if="viewer?.is_owner && ownerViewMode && canOpenOwnerLogoModal"
            type="button"
            class="mb-4 block max-w-[200px] rounded border-0 bg-transparent p-0 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900/80"
            :aria-label="$t('public.ownerEditLogoAria')"
            @click="openOwnerLogoModal"
          >
            <div class="py-4 px-4 rounded border border-dashed border-white/40 bg-white/10">
              <p class="text-sm font-mono text-white/80">{{ $t('public.noLogoSetOwnerMessage') }}</p>
            </div>
          </button>
          <div v-else-if="viewer?.is_owner && ownerViewMode" class="mb-4 max-w-[200px] py-4 px-4 rounded border border-dashed border-white/40 bg-white/10">
            <p class="text-sm font-mono text-white/80">{{ $t('public.noLogoSetOwnerMessage') }}</p>
          </div>
          <PublicOpenStatusBadge :operating-hours="operatingHours" variant="t1-hero" />
          <h1 class="text-4xl md:text-6xl font-bold mb-4">{{ name || 'Restaurant' }}</h1>
          <p v-if="tagline" class="text-lg md:text-xl font-light opacity-90">{{ tagline }}</p>
          <p v-if="description && !tagline" class="text-lg md:text-xl font-light opacity-90">{{ description }}</p>
          <p v-else-if="description" class="mt-2 text-base md:text-lg font-light opacity-85">{{ description }}</p>
          <div v-else-if="viewer?.is_owner && ownerViewMode" class="max-w-md py-4 px-4 rounded border border-dashed border-white/40 bg-white/10">
            <p class="text-sm font-mono text-white/80">{{ $t('public.noTaglineOrDescriptionSetOwnerMessage') }}</p>
          </div>
          <div class="flex flex-wrap gap-3 mt-6">
            <a
              href="#menu"
              class="inline-flex min-h-[44px] cursor-pointer items-center justify-center rounded-lg bg-[var(--t1-primary)] px-6 py-2.5 text-sm font-bold text-white transition-opacity duration-200 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--t1-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
            >
              {{ viewListLabel || $t('public.t1NavMenu') }}
            </a>
            <a
              v-if="hasAbout"
              href="#about"
              class="inline-flex min-h-[44px] cursor-pointer items-center justify-center rounded-lg border border-white/50 px-6 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
            >
              {{ $t('public.t3NavAbout') }}
            </a>
            <div
              v-else-if="viewer?.is_owner && ownerViewMode"
              class="py-2.5 px-4 rounded-lg border border-dashed border-white/40 bg-white/10 max-w-[200px]"
            >
              <p class="text-xs font-mono text-white/80">{{ $t('public.ownerNavAboutUnavailable') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import PublicOpenStatusBadge from '@/components/public/PublicOpenStatusBadge.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()

const props = defineProps({
  name: { type: String, default: '' },
  tagline: { type: String, default: '' },
  description: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  bannerUrl: { type: String, default: '' },
  showOwnerPlaceholder: { type: Boolean, default: false },
  operatingHours: { type: Object, default: null },
  viewListLabel: { type: String, default: '' },
  hasAbout: { type: Boolean, default: false },
  viewer: { type: Object, default: () => ({ is_owner: false }) },
  ownerViewMode: { type: Boolean, default: true },
})
</script>
